import React, { useState, useRef, useEffect, useCallback } from "react";
import "./GroupSelector.css";

// Group colors palette
const GROUP_COLORS = {
  1: "#ef4444", // Red
  2: "#f97316", // Orange
  3: "#eab308", // Yellow
  4: "#22c55e", // Green
  5: "#3b82f6", // Blue
};

const LinkIcon = ({ size = 10 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6.5 9.5a3.5 3.5 0 0 0 5 0l2-2a3.5 3.5 0 0 0-5-5l-1 1" />
    <path d="M9.5 6.5a3.5 3.5 0 0 0-5 0l-2 2a3.5 3.5 0 0 0 5 5l1-1" />
  </svg>
);

/**
 * GroupBadge - Visual indicator of the current group (shown in tab header)
 * Clicking opens the GroupSelector popup
 */
export function GroupBadge({ group, onClick, className = "" }) {
  const color = group ? GROUP_COLORS[group] : null;

  return (
    <button
      className={`group-badge ${!group ? "group-badge--ungrouped" : ""} ${className}`}
      onClick={onClick}
      style={group ? { backgroundColor: color } : undefined}
      title={group ? `Grupo ${group}` : "Sem grupo (clique para vincular)"}
      data-evo-id="groupselector-btn-open"
    >
      {group ? group : <LinkIcon />}
    </button>
  );
}

/**
 * GroupSelector - Popup for selecting a linking group (1-5 or none)
 */
export function GroupSelector({
  chartId,
  currentGroup,
  onSelect,
  onClose,
  anchorRef,
}) {
  const popupRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Position popup below the anchor element
  useEffect(() => {
    if (anchorRef?.current && popupRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: anchorRect.bottom + 4,
        left: anchorRect.left,
      });
    }
  }, [anchorRef]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleSelect = useCallback(
    (group) => {
      onSelect(chartId, group);
      onClose();
    },
    [chartId, onSelect, onClose],
  );

  return (
    <div
      ref={popupRef}
      className="group-selector-popup"
      style={{ top: position.top, left: position.left }}
    >
      <div className="group-selector-title">Grupo</div>
      <div className="group-selector-options">
        {/* No group option */}
        <button
          className={`group-selector-option ${currentGroup === null ? "selected" : ""}`}
          onClick={() => handleSelect(null)}
          title="Sem grupo"
          data-evo-id="groupselector-btn-none"
        >
          <LinkIcon size={12} />
        </button>
        {/* Group 1-5 options */}
        {[1, 2, 3, 4, 5].map((num) => (
          <button
            key={num}
            className={`group-selector-option ${currentGroup === num ? "selected" : ""}`}
            onClick={() => handleSelect(num)}
            style={{ backgroundColor: GROUP_COLORS[num] }}
            title={`Grupo ${num}`}
            data-evo-id={`groupselector-btn-group-${num}`}
          >
            {num}
          </button>
        ))}
      </div>
    </div>
  );
}

export { GROUP_COLORS };
